import { useEffect, useRef, useState } from "react";
import { DownloadSimple, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function Shot({ s, onDelete }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    api.get(`/admin/screenshots/${s.id}/file`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [s.id]);
  return (
    <figure className="glass p-3 group" data-testid={`screenshot-card-${s.id}`}>
      <a href={url || undefined} target="_blank" rel="noopener" className="block aspect-[16/10] overflow-hidden rounded-lg bg-muted">
        {url ? <img src={url} alt={s.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]" data-testid={`screenshot-img-${s.id}`} /> : <div className="w-full h-full animate-pulse" />}
      </a>
      <figcaption className="flex items-center justify-between gap-2 mt-3">
        <div className="min-w-0"><p className="text-sm font-medium truncate" data-testid={`screenshot-title-${s.id}`}>{s.title}</p><p className="text-[11px] text-muted-foreground font-mono-num">{(s.size / 1024).toFixed(0)} KB · {new Date(s.created_at).toLocaleDateString()}</p></div>
        <div className="flex shrink-0">
          <Button asChild variant="ghost" size="icon" disabled={!url}><a href={url} download={`${s.title}.${s.content_type.split("/")[1]}`} aria-label="Download" data-testid={`screenshot-download-${s.id}`}><DownloadSimple size={16} /></a></Button>
          <Button variant="ghost" size="icon" onClick={() => onDelete(s)} aria-label="Delete" data-testid={`screenshot-delete-${s.id}`}><Trash size={16} /></Button>
        </div>
      </figcaption>
    </figure>
  );
}

export default function AdminScreenshots() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => api.get("/admin/screenshots").then((r) => setItems(r.data.items)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);
  if (user?.role !== "admin") return <p className="text-sm text-muted-foreground" data-testid="admin-forbidden">This page is for admins only.</p>;
  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is larger than 10 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("title", f.name.replace(/\.[^.]+$/, ""));
      try { await api.post("/admin/screenshots", fd); } catch (e) { toast.error(errMsg(e)); }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    load();
  };
  const confirmDelete = async () => {
    try { await api.delete(`/admin/screenshots/${del.id}`); toast.success("Screenshot deleted"); setItems((x) => x.filter((i) => i.id !== del.id)); } catch (e) { toast.error(errMsg(e)); }
    setDel(null);
  };
  return (
    <div>
      <PageHeader title="App Screenshots" subtitle="Page screenshots of PillarWealth, saved in file storage. Admins only."
        actions={<><input ref={input} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => upload([...e.target.files])} data-testid="screenshot-upload-input" />
          <Button className="rounded-full" disabled={busy} onClick={() => input.current?.click()} data-testid="screenshot-upload-button"><UploadSimple size={16} className="mr-1.5" />{busy ? "Uploading..." : "Upload"}</Button></>} />
      {!items ? <p className="text-sm text-muted-foreground">Loading...</p> : !items.length ? <p className="text-sm text-muted-foreground" data-testid="screenshots-empty">No screenshots yet.</p> : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="screenshots-grid">{items.map((s) => <Shot key={s.id} s={s} onDelete={setDel} />)}</div>
      )}
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent data-testid="screenshot-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this screenshot?</AlertDialogTitle><AlertDialogDescription>"{del?.title}" will be removed from your gallery.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="screenshot-delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} data-testid="screenshot-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
